import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { CommandClient } from '../client/types';
import { createApi, type Api } from '../api';

interface Ctx {
  client: CommandClient;
  api: Api;
}
const ClientContext = createContext<Ctx | null>(null);

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: false },
      mutations: { retry: 0 },
    },
  });
}

export function ClientProvider({ client, queryClient, children }: { client: CommandClient; queryClient?: QueryClient; children: ReactNode }) {
  const qc = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);
  const value = useMemo(() => ({ client, api: createApi(client) }), [client]);
  return (
    <ClientContext.Provider value={value}>
      <QueryClientProvider client={qc}>{children}</QueryClientProvider>
    </ClientContext.Provider>
  );
}

export function useClient(): CommandClient {
  const c = useContext(ClientContext);
  if (!c) throw new Error('useClient: wrap the tree in <ClientProvider>');
  return c.client;
}
export function useApi(): Api {
  const c = useContext(ClientContext);
  if (!c) throw new Error('useApi: wrap the tree in <ClientProvider>');
  return c.api;
}

/** Resolve "self" for the local shell, pass-through for the cloud. */
export function useDeviceId(id?: string): string {
  const client = useClient();
  return id ?? client.selfDeviceId ?? 'self';
}

export const qk = {
  me: ['me'] as const,
  authStatus: ['authStatus'] as const,
  devices: (p?: object) => ['devices', p ?? {}] as const,
  device: (id: string) => ['device', id] as const,
  status: (id: string) => ['status', id] as const,
  settings: (id: string) => ['settings', id] as const,
  profiles: (id: string) => ['profiles', id] as const,
  security: (id: string) => ['security', id] as const,
  network: (id: string) => ['network', id] as const,
  update: (id: string) => ['update', id] as const,
  logs: (id: string) => ['logs', id] as const,
  wordlib: (id: string) => ['wordlib', id] as const,
  cloud: (id: string) => ['cloud', id] as const,
  sysinfo: (id: string) => ['sysinfo', id] as const,
  recordings: (id?: string) => ['recordings', id ?? '*'] as const,
  alerts: (open?: boolean) => ['alerts', !!open] as const,
  alertChannels: ['alertChannels'] as const,
  releases: (ch: string, kind = 'app') => ['releases', ch, kind] as const,
  rollout: (id: string) => ['rollout', id] as const,
  rollouts: ['rollouts'] as const,
  members: ['members'] as const,
  audit: ['audit'] as const,
  events: ['events'] as const,
};
